// Copyright (c) Microsoft. All rights reserved.

import { Dispatch, MiddlewareAPI, configureStore, AnyAction } from '@reduxjs/toolkit';
import { persistStore, persistReducer, PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { AppState } from '../features/app/AppState';
import { ConversationsState } from '../features/conversations/ConversationsState';
import { signalRMiddleware } from '../features/message-relay/signalRMiddleware';
import { StreamingSliceState } from '../features/streaming/streamingSlice';
import { UsersState } from '../features/users/UsersState';
import resetStateReducer, { resetApp } from './rootReducer';

export interface RootState {
    app: AppState;
    conversations: ConversationsState;
    users: UsersState;
    streaming: StreamingSliceState;
}

// Persist configuration with proper typing
const persistConfig: PersistConfig<RootState> = {
    key: 'root',
    storage,
    whitelist: ['app'], // Only persist the app slice (contains dark mode setting)
    blacklist: ['conversations', 'users'], // Don't persist these slices
};

const persistedReducer = persistReducer(persistConfig, resetStateReducer);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
            },
        }).concat(signalRMiddleware),
});

export type StoreMiddlewareAPI = MiddlewareAPI<Dispatch<AnyAction>, RootState>;
export type Store = typeof store;

export const persistor = persistStore(store);

export const getSelectedChatID = (): string => {
    const state = store.getState() as RootState;
    return state.conversations.selectedId;
};

export type AppDispatch = typeof store.dispatch;

// Function to reset the app state
export const resetState = (): void => {
    store.dispatch(resetApp());
};
